import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { 
    CheckCircleIcon as CheckCircle, 
    AlertCircleIcon as AlertCircle, 
    RefreshIcon as Refresh,
    MailIcon as Mail,
    SendIcon as Send,
    PlusIcon as Plus
} from './Icons';

const CloudflareUserStatus = ({ profile, onUpdate }) => {
    const { user, API_URL } = useContext(AuthContext);
    const [status, setStatus] = useState({
        routing: null,
        destination: null,
        loading: true,
        error: null
    });

    const fetchStatus = async () => {
        if (!user || !user.token || !profile) return;
        try {
            setStatus(prev => ({ ...prev, loading: true, error: null }));
            const headers = { Authorization: `Bearer ${user.token}` };
            
            // Fetch routings with proper error handling
            let routingsRes = { data: [] };
            try {
                routingsRes = await axios.get(`${API_URL}/cloudflare/routings`, { headers });
            } catch (err) {
                const errMsg = err.response?.data?.message || err.message || '';
                // Configuration errors need admin attention
                if (err.response?.status === 500 && errMsg.includes('[CONFIG]')) {
                    throw new Error('Cloudflare ist nicht konfiguriert. Bitte kontaktieren Sie einen Administrator.');
                }
                if (err.response?.status === 403) {
                    throw new Error(errMsg || 'Cloudflare API Token hat zu wenig Berechtigungen.');
                }
                // 401 means user is not admin - this is expected for non-admin users
                if (err.response?.status === 401) {
                    console.log('User is not admin, Cloudflare features unavailable');
                }
                // Otherwise continue with empty data
            }
            
            // Fetch destinations with proper error handling
            let destRes = { data: [] };
            try {
                destRes = await axios.get(`${API_URL}/cloudflare/destinations`, { headers });
            } catch (err) {
                const errMsg = err.response?.data?.message || err.message || '';
                const status = err.response?.status;
                if (status === 403) {
                    throw new Error(errMsg || 'Cloudflare API Token hat zu wenig Berechtigungen.');
                }
                // Silently fail for destinations if not authorized or not configured
                if (status !== 401 && status !== 500) {
                    console.warn('Failed to fetch Cloudflare destinations:', err.message);
                }
            }

            const routings = Array.isArray(routingsRes.data) ? routingsRes.data : [];
            const destinations = Array.isArray(destRes.data) ? destRes.data : [];

            // Find specific ones for this user
            const userRouting = routings.find(r => 
                r.matchers?.[0]?.value === profile.officialEmail || 
                r.actions?.[0]?.value?.[0] === profile.email
            );
            
            const userDestination = destinations.find(d => d.email === profile.email);

            setStatus({
                routing: userRouting,
                destination: userDestination,
                loading: false,
                error: null
            });
        } catch (err) {
            console.error('Error fetching Cloudflare status:', err);
            const msg = err.message || err.response?.data?.message || 'Status konnte nicht geladen werden.';
            setStatus(prev => ({ 
                ...prev, 
                loading: false, 
                error: msg
            }));
        }
    };

    useEffect(() => {
        fetchStatus();
    }, [profile?.email, profile?.officialEmail]);

    const handleSetup = async () => {
        try {
            setStatus(prev => ({ ...prev, loading: true }));
            const headers = { Authorization: `Bearer ${user.token}` };
            const { data } = await axios.post(`${API_URL}/cloudflare/setup-user/${profile._id}`, {}, { headers });
            
            if (data.status === 'verification_pending') {
                alert(data.message);
            } else {
                if (onUpdate) {
                    // Fetch full user again to be sure
                    const userRes = await axios.get(`${API_URL}/users/${profile._id}`, { headers });
                    onUpdate(userRes.data);
                }
                alert('Cloudflare Routing wurde erfolgreich eingerichtet: ' + data.officialEmail);
            }
            
            await fetchStatus();
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            alert('Fehler beim Setup: ' + msg);
            setStatus(prev => ({ ...prev, loading: false, error: msg }));
        }
    };

    const handleVerify = async () => {
        try {
            setStatus(prev => ({ ...prev, loading: true }));
            const headers = { Authorization: `Bearer ${user.token}` };
            await axios.post(`${API_URL}/cloudflare/destinations`, { email: profile.email }, { headers });
            await fetchStatus();
            alert('Verifizierungs-Email wurde an ' + profile.email + ' gesendet.');
        } catch (err) {
            alert('Fehler beim Senden der Email.');
            setStatus(prev => ({ ...prev, loading: false }));
        }
    };

    if (status.loading && !status.routing && !status.destination) {
        return <div className="p-4 animate-pulse bg-surface-container rounded-2xl h-24" />;
    }

    const isRoutingActive = status.routing && status.routing.enabled;
    const isVerified = status.destination && status.destination.verified;

    return (
        <div className="md3-card-outlined !bg-surface-containerLow border-primary/20 overflow-hidden">
            <div className="p-4 bg-primary/5 border-b border-primary/10 flex items-center justify-between">
                <h4 className="text-label-large font-bold text-primary flex items-center gap-2">
                    <Mail size={16} />
                    Cloudflare Email Routing
                </h4>
                <button 
                    onClick={fetchStatus} 
                    className="md3-icon-btn w-8 h-8 hover:bg-primary/10"
                    disabled={status.loading}
                >
                    <Refresh size={14} className={status.loading ? 'animate-spin' : ''} />
                </button>
            </div>

            <div className="p-5 space-y-4">
                {/* Error Message */}
                {status.error && (
                    <div className="px-3 py-2 bg-error/10 border border-error/20 rounded-lg flex items-start gap-2">
                        <AlertCircle size={16} className="text-error flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-error flex-1">
                            {status.error}
                        </div>
                    </div>
                )}

                {/* Status Indicator */}
                <div className="flex flex-wrap gap-3">
                    {/* Routing Status */}
                    <div className={`px-3 py-1.5 rounded-full flex items-center gap-2 text-xs font-bold ${
                        isRoutingActive ? 'bg-success/10 text-success' : 'bg-surface-containerHighest text-surface-onVariant'
                    }`}>
                        {isRoutingActive ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                        {isRoutingActive ? 'Weiterleitung Aktiv' : 'Keine Weiterleitung'}
                    </div>

                    {/* Verification Status */}
                    <div className={`px-3 py-1.5 rounded-full flex items-center gap-2 text-xs font-bold ${
                        isVerified ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                    }`}>
                        {isVerified ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                        {isVerified ? 'Ziel-Mail Bestätigt' : 'Ziel-Mail Ausstehend'}
                    </div>
                </div>

                {/* Details */}
                <div className="space-y-2 py-2">
                    {profile.officialEmail && (
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-surface-onVariant">Offizielle Adresse:</span>
                            <span className="font-bold text-primary">{profile.officialEmail}</span>
                        </div>
                    )}
                    <div className="flex justify-between items-center text-sm">
                        <span className="text-surface-onVariant">Ziel-Adresse:</span>
                        <span className="font-medium text-surface-on">{profile.email}</span>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                    {!isRoutingActive ? (
                        <button 
                            onClick={handleSetup}
                            disabled={status.loading}
                            className="md3-btn-filled !rounded-xl !py-2 !px-4 text-xs gap-2"
                        >
                            <Plus size={14} /> {status.destination ? 'Routing Abschließen' : 'Routing Einrichten'}
                        </button>
                    ) : (
                        !isVerified && (
                            <button 
                                onClick={handleVerify}
                                disabled={status.loading}
                                className="md3-btn-tonal !rounded-xl !py-2 !px-4 text-xs gap-2"
                            >
                                <Send size={14} /> Bestätigung erneut senden
                            </button>
                        )
                    )}
                </div>
                
                {status.error && (
                    <div className="p-3 bg-error-container text-error rounded-xl text-[10px] font-medium border border-error/20">
                        {status.error}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CloudflareUserStatus;
